import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Lock } from 'lucide-react';
import { useActiveSubscription } from '@/hooks/useActiveSubscription';

interface Props {
  kind: 'listing' | 'project';
  /** When true, do not render an upsell (used during edit mode where quota is not consumed). */
  skip?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps an Add form. If the user has no remaining quota for the given kind,
 * shows a full-page upsell card instead of the form. Superadmins/staff bypass.
 */
export function QuotaUpsell({ kind, skip, children }: Props) {
  const sub = useActiveSubscription();

  if (skip || sub.isLoading) return <>{children}</>;

  const usage = kind === 'project' ? sub.projects : sub.listings;
  const ok = usage.quota > 0 && usage.remaining > 0;
  if (ok) return <>{children}</>;

  const noPlan = !sub.hasActive || usage.quota === 0;
  return (
    <div className="max-w-2xl mx-auto py-10">
      <Card className="border-2 border-dashed">
        <CardContent className="p-8 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            {noPlan ? <Lock className="h-7 w-7 text-primary" /> : <Sparkles className="h-7 w-7 text-primary" />}
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">
              {noPlan ? `Subscribe to add ${kind === 'project' ? 'projects' : 'listings'}` : `You've reached your ${kind} quota`}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {noPlan
                ? `Your account doesn't have an active package with ${kind} slots. Choose a plan to start posting.`
                : `You've used ${usage.used} of ${usage.quota} ${kind} slots on your current plan. Upgrade to add more.`}
            </p>
          </div>
          <div className="flex justify-center gap-2 pt-2">
            <Button asChild>
              <Link to="/dashboard/subscription">{noPlan ? 'View Packages' : 'Upgrade Plan'}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
